/**
 * @copyright Copyright 2016-2018 The MathWorks, Inc.
 * @description This module is a newable object which contains the dialog creation life cycle and
 * its APIs
 * @module mw-dialogs/Dialog
 */
define([
    "dojo/dom-class",
    "dojo/dom-style",
    "dojo/has",
    "dojo/keys",
    "dojo/on",
    "dojo/touch",
    "dojo/i18n!mw-dialogs/l10n/nls/dialog",
    "mw-dialogs/enums/RoleEnum",
    "mw-dialogs/controller/DialogController",
    "mw-dialogs/controller/Draggable",
    "mw-dialogs/controller/Resizable",
    "mw-dialogs/controller/Focus",
    "mw-dialogs/controller/stopEvent",
    "mw-dialogs/view/ButtonBar",
    "mw-dialogs/view/ContentWidget",
    "mw-dialogs/utils/preventShortCutKeys"
], function (domClass, domStyle, has, keys, on, touch, dialogL10n, RoleEnum,
             dialogController, Draggable, Resizable, Focus, stopEvent, ButtonBar,
             ContentWidget, preventShortCutKeys) {


    let preventScrollingBackground = (element) => {
        return on(element, (!has("mozilla") ? "mousewheel" : "DOMMouseScroll"), (e) => {
            let delta = (e.type === 'DOMMouseScroll' ? e.detail * -40 : e.wheelDelta),
                up = delta > 0;

            if (!up &&
                -delta > element.scrollHeight - element.clientHeight - element.scrollTop) {
                element.scrollTop = element.scrollHeight;
                stopEvent(e);
            } else if (up && delta > element.scrollTop) {
                element.scrollTop = 0;
                stopEvent(e);
            }
        });
    };

    /**
     * @class Dialog
     * @description Dialog constructor
     * @param {object} options - options used to create different kinds of dialog.
     * @private
     */
    class Dialog {
        /**
         * @event show
         * @description show event emitted when dialog displays.
         * @type {object}
         * @example <caption>Example of listening on a dialog show event:</caption>
         * dialog.on("show", function (e) {
         *     console.log("show");
         * });
         */

        /**
         * @event close
         * @description close event emitted when dialog is closed.
         * @type {object}
         * @example <caption>Example of listening on a dialog close event:</caption>
         * dialog.on("close", function (e) {
         *     console.log("closed");
         * });
         */

        /**
         * @event resize
         * @description resize event emitted when dialog is being resized.
         * @type {object}
         * @property {object} event.dimension - Current dimension of dialog with x, y, w, h which
         * indicates left, top, width , height information
         * @property {object} event.contentSize - Current size of dialog content area with
         * w, h which indicates the width and height
         * @example <caption>Example of listening on a dialog resize event:</caption>
         * dialog.on("resize", function (e) {
         *     console.log("Dialog demension: ", e.dimension);
         *     console.log("Content Size: ", e.contentSize);
         * });
         */

        /**
         * @event move
         * @description move event emitted when dialog is being dragged.
         * @type {object}
         * @property {object} event.position - An xy based position from left and top
         * which indicates the position of the dialog
         * @example <caption>Example of listening on a dialog drag event:</caption>
         * dialog.on("move", function (e) {
         *     console.log("moved: ", e.position);
         * });
         */

        /**
         * @event (Type)ButtonClick
         * @description buttonClickEvent event emitted when a button is being clicked.
         * @type {object}
         * @property {function} event.close - The close callback function when button is clicked
         * @example <caption>Example of listening on a button click event:</caption>
         * dialog.addButtonEventListener(ButtonEnum.TYPE.CANCEL, function (e) {
         *     console.log("CancelButtonClick");
         * }, true);
         */

        constructor(options) {
            this._eventSignals = [];
            this.domNode = this._createDomNodes({
                id: options.id,
                dialogType: options.dialogType,
                role: options.role,
                uuid: options.uuid
            });

            this.domNode.widget = this;

            this._buildDialog(options);

            this._renderDialog(options.title);

            this._show();
            return this;
        }

        static numberOfOpenDialogs() {
            let count = {};
            count.modal = Dialog.prototype._allModalDialogs.length;
            count.modeless = Dialog.prototype._allModelessDialogs.length;
            return count;
        }

        addButtonEventListener(button, callBackFunction, dismiss) {
            /**
             * @function addButtonEventListener
             *
             * @description Registers an event listener for button click event.
             *
             * @instance
             *
             * @param {Object} button - Button or Type of the button which the event listener be
             * registered.  E.g., ButtonEnum.OK, ButtonEnum.TYPE.DO_IT
             * @param {Object} callBackFunction - The callback function when clicking on the button
             * @param {Boolean} dismiss - Whether to close the dialog when click on the button
             *
             * @example
             * dialog.addButtonEventListener(ButtonEnum.TYPE.DO_IT, function(e) {
             *     printToOutputArea("CreateButtonClick");
             * }, true);
             */
            ButtonBar.addButtonEventListener(this, button, callBackFunction, dismiss);
        }

        enableButton(button) {
            /**
             * @function enableButton
             * @instance
             * @description Enables an action or supplemental button, allowing interaction.
             * @param {Object} button - Button or Type of the button which is used to tell
             *     which button in dialog to be set as enabled.  E.g., ButtonEnum.OK,
             *     ButtonEnum.TYPE.DO_IT
             *
             * @example
             * dialog.enableButton(ButtonEnum.TYPE.DO_IT);
             */
            ButtonBar.enableButton(this, button);
        }

        disableButton(button) {
            /**
             * @function disableButton
             * @instance
             * @description Disables an action or supplemental button, preventing interaction.
             * @param {Object} button - Button or Type of the button which is used to tell
             *     which button in dialog to be set as disabled.  E.g., ButtonEnum.OK,
             *     ButtonEnum.TYPE.DO_IT
             *
             * @example
             * dialog.disableButton(ButtonEnum.TYPE.DO_IT);
             */
            ButtonBar.disableButton(this, button);
        }

        on(event, func) {
            /**
             * @function on
             * @description Registers an event listener on the dialog
             * @instance
             * @param {String} event - JavaScript event
             * @param {Object} func - callback function
             * @example dialog.on('click', function (e) {
             *     console.log("ok!")
             * }));
             */
            let eventSignal = on(this.domNode, event, func);
            this._eventSignals.push(eventSignal);
        }

        close() {
            /**
             * @function close
             *
             * @description Closes and destroys the dialog. Emits the "close" event.
             * @example dialog.close();
             * @instance
             */
            on.emit(this.domNode, "close", {
                bubbles: false,
                cancelable: true
            });
            this.destroy();
        }

        destroy() {
            /**
             * @function destroy
             *
             * @description Destroys this dialog completely.
             * Destroys the dialog and its children. Removes the dialog functionality completely.
             * This will return the dialog elements back to its pre-init state. Restores the
             * focus back to the previous focused item.
             * @example dialog.destroy();
             * @instance
             */
            if (!this.domNode) {
                // if dialog is already destroyed, then won't execute the destroy code below
                return;
            }

            /**
             * g1706846, g1830624: Dialogs should restore focus to the last focused element when
             * they're dismissed, via the focus manager.
             */
            on.emit(this.domNode, "restorefocus", {bubbles: true});

            if (this._isModal) {
                preventShortCutKeys.removePrevent(this);
                this._allModalDialogs.splice(this._allModalDialogs.indexOf(this), 1);
            } else {
                this._allModelessDialogs.splice(this._allModelessDialogs.indexOf(this), 1);
            }

            if (this._focusHandle) {
                this._focusHandle.unwatch();
            }

            // Clean up all the event listeners when dialog is destroyed
            this._eventSignals.forEach((eventSignal) => {
                eventSignal.remove();
            });
            this._eventSignals = [];

            let key;
            for (key in this._buttonEventHandlers) {
                this._buttonEventHandlers[key].remove();
            }

            this._buttonEventHandlers = {};

            if (this._resizeEventSignals) {
                Resizable.disable(this);
            }

            if (this._contentWidget) {
                this._contentWidget.destroyRecursive();
            }

            document.body.removeChild(this.domNode);

            if (this._isModal === true) {
                document.body.removeChild(this._underlay);
                // Make sure only the most top underlay displays the background color
                let remainingModalDialogs = this._allModalDialogs;
                if (remainingModalDialogs.length > 1) {
                    remainingModalDialogs.sort((a, b) => {
                        return a.domNode.style.zIndex - b.domNode.style.zIndex;
                    });
                }
                if (remainingModalDialogs.length >= 1) {
                    remainingModalDialogs[remainingModalDialogs.length - 1]._underlay.style.opacity
                        = 1;
                }
            }

            for (key in this) {
                if (this.hasOwnProperty(key)) {
                    delete this[key];
                }
            }
        }

        moveTo(position) {
            /**
             * @function moveTo
             *
             * @description Places the dialog at center or an xy based position from left and top.
             *
             * @instance
             *
             * @param {Object} position - An object containing the x and y values of the location to
             * place the dialog at. E.g, {x: 0, y: 0} If it is not specified, dialog is positioned
             * at the center.
             *
             * @example dialog.moveTo({x: 0, y: 0});
             */
            dialogController.moveTo(this, position);
        }

        resizeTo(options) {
            /**
             * @function resizeTo
             *
             * @desc Changes the height and width of the dialog, without a change in dialog position
             *
             * @instance
             *
             * @param {Object} options - An object containing properties affecting resizeTo behavior
             * @param {Number} [options.height] - Y-length in pixels
             * @param {Number} [options.width] - X-length in pixels
             *
             * @example dialog.resizeTo({height: 600, width: 760})
             *
             */
            dialogController.resizeTo(this, options);
        }

        bringToTop() {
            /**
             * Bring this dialog above all other dialogs
             *
             * @instance
             */
            let dialogs = [];
            let that = this;
            if (this._isModal) {
                dialogs = this._allModalDialogs.filter(
                    (dialog) => {
                        return dialog !== that;
                    },
                    this);
                dialogs.sort((a, b) => {
                    return a.domNode.style.zIndex - b.domNode.style.zIndex;
                });
                dialogs.push(this);

                dialogs.forEach((dialog, index) => {
                    dialog.domNode.style.zIndex = that._startZ + (index * 2);
                    dialog._underlay.style.zIndex = dialog.domNode.style.zIndex - 1;
                    if (index !== dialogs.length - 1) {
                        dialog._underlay.style.opacity = 0;
                    }
                    domClass.remove(dialog.domNode, "mwModalDialogFg");
                }, this);
                domClass.add(this.domNode, "mwModalDialogFg");
            } else {
                dialogs = this._allModelessDialogs.filter(
                    (dialog) => {
                        return dialog !== that;
                    },
                    this);

                /**
                 * g1705201: handle the z-index issue where dialogs created from the
                 * mw-dialogs-utils and mw-dialogs both are on the page
                 */
                let nonModalDialogs = document.getElementsByClassName("mwNonModalDialog");
                if (nonModalDialogs.length > 0) {
                    nonModalDialogs = Array.prototype.slice.call(nonModalDialogs);
                    nonModalDialogs = nonModalDialogs.map((dialog) => {
                        return {domNode: dialog};
                    });
                    dialogs = dialogs.concat(nonModalDialogs);
                }

                dialogs.sort((a, b) => {
                    return a.domNode.style.zIndex - b.domNode.style.zIndex;
                });
                dialogs.push(this);

                dialogs.forEach((dialog, index) => {
                    dialog.domNode.style.zIndex = that._startZ + (index * 2);
                    domClass.remove(dialog.domNode, "mwModelessDialogFg");
                    if (nonModalDialogs.length > 0) {
                        domClass.remove(dialog.domNode, "dojoxFloatingPaneFg");
                    }
                }, this);
                domClass.add(this.domNode, "mwModelessDialogFg");
            }
        }

        set title(text) {
            /**
             * @function title setter
             * @instance
             * @property {String} text - set title text in dialog
             * @example <caption>Set dialog title:</caption>
             * dialog.title = "Title";
             */
            this._titleText = "";

            if (text && typeof(text) === "string") {
                this._titleText = text;
            }

            this._titleBar._titleNode.textContent = this._titleText;

            // if title has ellipsis, show tooltip
            if (this._titleBar._titleNode.offsetWidth < this._titleBar._titleNode.scrollWidth) {
                this._titleBar._titleNode.title = this._titleText;
            }
        }

        get title() {
            /**
             * @function title getter
             * @instance
             * @returns {String} - title of dialog
             * @example <caption>Get dialog title:</caption>
             * let title = dialog.title;
             */
            return this._titleText;
        }

        _show() {
            /**
             * @function _show
             * @description Displays the dialog onto viewport
             *
             * @private
             */
            domStyle.set(this.domNode, "visibility", "visible");

            this.moveTo(this._specifiedPosition);

            Focus.focus(this);

            if (this._contentWidget) {
                this._contentWidget.startup();
            }

            on.emit(this.domNode, "show", {
                bubbles: false,
                cancelable: true
            });
        }

        _trackEventListener(eventSignal) {
            /**
             * @function _trackEventListener
             * @description Keeps track of event listener registered for dialog.
             * @private
             * @param {Object} eventSignal - dojo.on callback
             * @example dialog._trackEventListener(on(closeNode, 'click', function (e) {
             *     dialog.close();
             * }));
             */
            this._eventSignals.push(eventSignal);
            return eventSignal;
        }

        _removeEventListener(eventSignal) {
            /**
             * @function _removeEventListener
             * @description Remove an event listener registered for dialog from the
             * _eventSignals list
             * @private
             * @param {Object} eventSignal - dojo.on callback
             * @example dialog._removeEventListener(eventSignal);
             */
            let index = this._eventSignals.indexOf(eventSignal);
            if (index > -1) {
                this._eventSignals.splice(index, 1);
                eventSignal.remove();
            }
        }

        _createDomNodes(options) {
            /**
             * create dom nodes in dialog
             *
             * @private
             */
            let {dialogType, id, role, uuid} = options;
            let dialog = document.createElement("div");
            dialog.id = id;
            dialog.className = 'mwDialog';
            dialog.tabIndex = "0";
            if (dialogType === 'modal') {
                dialog.setAttribute('aria-modal', true);
                role = role === RoleEnum.ALERT_DIALOG ? role : RoleEnum.DIALOG;
            } else {
                dialog.setAttribute('aria-modal', false);
                role = RoleEnum.DIALOG;
            }

            dialog.setAttribute('role', role);

            let titleBar = this._titleBar = document.createElement("div");
            titleBar.className = 'mwDialogTitleBar';
            titleBar.setAttribute("data-test-id", "dialog-title-bar");

            let titleNode = this._titleBar._titleNode = document.createElement("span");
            titleNode.className = 'mwTitleNode';
            titleNode.id = "dialogTitle_" + uuid;
            titleNode.setAttribute("data-test-id", "dialog-title-node");

            let contentArea = this._contentArea = document.createElement("div");
            contentArea.id = "dialogContent_" + uuid;


            contentArea.className = 'mwDialogBody';
            contentArea.setAttribute("data-test-id", "dialog-content-area");

            titleBar.appendChild(titleNode);
            dialog.appendChild(titleBar);
            dialog.appendChild(contentArea);
            dialog.setAttribute("aria-labelledby", titleNode.id);
            dialog.setAttribute("aria-describedby", contentArea.id);

            return dialog;
        }

        _buildDialog(options) {
            /**
             * build dialog based options passed in
             *
             * @private
             */
            let that = this;

            /**
             * g1706846: Dialogs should restore focus to the last focused element when they're
             * dismissed, via the focus manager.
             */
            this.domNode.setAttribute("data-focus", "temporary");

            if (options.className) {
                domClass.add(this.domNode, options.className);
            }

            if (!options.hideCloseButton) {
                // Create the close icon node
                let closeNode = this._closeNode = document.createElement("span");
                closeNode.className = 'mwCloseNode';
                closeNode.title = dialogL10n.close;
                closeNode.setAttribute("tabindex", "0");
                closeNode.setAttribute("role", "button");
                closeNode.setAttribute("data-test-id", "dialog-close-node");
                let closeSVG = this._closeSVG = document.createElement("span");
                closeSVG.className = 'mwCloseSVG';
                closeNode.appendChild(closeSVG);
                this._titleBar.appendChild(closeNode);
                this._trackEventListener(on(closeNode, 'click', () => {
                    that.close();
                }));
            }

            if (options.closeOnEscape) {
                this.on('keydown', (e) => {
                    if (e.keyCode === keys.ESCAPE) {
                        that.close();
                        stopEvent(e);
                    }
                });
            }

            if (options.content) {
                this._contentWidget = new ContentWidget({
                    widget: options.content,
                    contentArea: this._contentArea
                });
                dialogController.selectionWithinDialogBounds(this);
            }


            if (options.buttons && Array.isArray(options.buttons) &&
                options.buttons.length > 0) {
                let bar = ButtonBar.createButtonBar(this, options.buttons,
                    options.defaultActionButton);
                this.domNode.appendChild(bar);
            } else {
                domClass.add(this._contentArea, 'mwNoButtons');
            }

            if (options.dialogType === 'modal') {
                this._isModal = true;
                this._startZ = this._modalDialogBeginZIndex;
                domClass.add(this.domNode, "mwModalDialog");
                let underlay = document.createElement("div");
                underlay.className = 'mwUnderlay';
                document.body.appendChild(underlay);
                this._underlay = underlay;
                preventShortCutKeys.prevent(this);
            } else {
                this._startZ = this._modelessDialogBeginZIndex;
                domClass.add(this.domNode, "mwModelessDialog");
            }

            if (options["data-test-id"]) {
                this.domNode.setAttribute("data-test-id", options["data-test-id"]);
            }

            if (options.bindingNode) {
                this._bindingNode = options.bindingNode;
            }

            if (options.position) {
                this._specifiedPosition = options.position;
            }

            if (options.specifiedFocusItem) {
                this.specifiedFocusItem = options.specifiedFocusItem;
            }

            this._addControllers(options);
        }

        _renderDialog(title) {
            /**
             * render dialog onto the document
             *
             * @private
             */

            // place DOM to the document
            document.body.appendChild(this.domNode);

            // Array of opening dialogs for keeping track of dialog z-index
            if (this._isModal) {
                this._allModalDialogs.push(this);
            } else {
                this._allModelessDialogs.push(this);
            }

            // Set min-width based on number of buttons in order to avoid buttons wrap
            if (this._buttonBar &&
                domStyle.get(this.domNode, "min-width") < this._buttonBar._buttonBarWidth) {
                domStyle.set(this.domNode, "min-width", `${this._buttonBar._buttonBarWidth}px`);
            }

            // Auto sizing based on dialog size after placing dialog onto the page
            // The size should not exceed the viewport size
            if (domStyle.get(this.domNode, "width") > document.documentElement.clientWidth) {
                domStyle.set(this.domNode, "width", `${document.documentElement.clientWidth}px`);
            } else {
                domStyle.set(this.domNode, "width", `${domStyle.get(this.domNode, "width")}px`);
            }
            if (domStyle.get(this.domNode, "height") > document.documentElement.clientHeight) {
                domStyle.set(this.domNode, "height", `${document.documentElement.clientHeight}px`);
            } else {
                domStyle.set(this.domNode, "height", `${domStyle.get(this.domNode, "height")}px`);
            }

            // Set dialog title after auto sizing dialog width
            // in order to avoid title length determine dialog width
            this.title = title;
        }

        _addControllers(options) {
            /**
             * add dialog controllers
             *
             * @private
             */

            let that = this;
            this._scrollWheelListener = null;

            if (options.draggable) {
                Draggable.draggable({
                    dialog: this,
                    bindingNode: this._bindingNode
                });
            } else {
                this._titleBar.style.cursor = "default";
            }

            if (options.resizable) {
                Resizable.enable({
                    widget: this,
                    bindingNode: this._bindingNode
                });

                // Bring dialog to the top when resizing
                this._resizeHandlers.forEach((resizeHandler) => {
                    that._resizeEventSignals.push(on(resizeHandler, touch.press, () => {
                        that.bringToTop();
                    }));
                });

                this._resizeEventSignals.push(on(this.domNode, Resizable.resizeEvent, (e) => {
                    that._isCenter = false;
                    that._specifiedPosition = {
                        x: e.dimension.x,
                        y: e.dimension.y
                    };

                    // Resolve scrollbar reflows content issue
                    that._resolveScrollBar();

                    // Resize header width when resizing the dialog
                    if (that._titleBar._titleNode.offsetWidth <
                        that._titleBar._titleNode.scrollWidth) {
                        that._titleBar._titleNode.title = that.title;
                    } else {
                        that._titleBar._titleNode.title = "";
                    }

                    if (that._contentArea.scrollHeight > that._contentArea.clientHeight) {
                        domClass.add(this._contentArea, 'mwOverflow');
                    } else {
                        domClass.remove(this._contentArea, 'mwOverflow');
                    }
                }));
            }

            this.on(touch.press, () => {
                that.bringToTop();
            });

            // If dialog has scrollbar initially
            this._resolveScrollBar();

            dialogController.addScrollHandler(this);
            dialogController.addBrowserResizeHandler(this);

        }

        _resolveScrollBar() {
            /**
             * resolve scroll bar in content causing content reflow issue
             *
             * @private
             */
            // Detect if there is a scrollbar showing up in the content area on any of the browsers
            if ((this._contentArea.scrollHeight - this._contentArea.clientHeight) > 0) {
                // Adjust the padding-right when a visible scrollbar showing up (windows and linux)
                if ((this._contentArea.offsetWidth - this._contentArea.scrollWidth) > 0) {
                    domStyle.set(this._contentArea, "padding-right",
                        `${this._standardContentPadding -
                        (this._contentArea.offsetWidth - this._contentArea.scrollWidth)}px`);
                }

                // Prevent scrolling the background when scrollbar appears in the content area
                if (!this._scrollWheelListener) {
                    this._scrollWheelListener = this._trackEventListener(
                        preventScrollingBackground(this._contentArea)
                    );
                }
            } else {
                // Reset the padding-right when scrollbar disappears
                domStyle.set(this._contentArea, "padding-right",
                    `${this._standardContentPadding}px`);
                // Remove the scroll event listener when the content area is not scrollable
                if (this._scrollWheelListener) {
                    this._removeEventListener(this._scrollWheelListener);
                    this._scrollWheelListener = null;
                }
            }
        }
    }

    Dialog.prototype._modalDialogBeginZIndex = 950;

    Dialog.prototype._modelessDialogBeginZIndex = 100;

    Dialog.prototype._standardContentPadding = 20;

    Dialog.prototype._allModalDialogs = [];

    Dialog.prototype._allModelessDialogs = [];

    return Dialog;
});
